import { LockOutlined, MailOutlined } from "@ant-design/icons";
import { Button, Card, Divider, Space, Tag, Tooltip, Typography } from "antd";
import type { ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import { useAuthStore } from "../../stores/authStore";
import {
  ACTION_DESCRIPTIONS,
  PERMISSION_MODULES,
  getPermissionSummary,
  hasPermission,
  permissionLabel,
  type PermissionAction
} from "../utils/permissions";

const { Title, Text, Paragraph } = Typography;

interface PermissionGateProps {
  /** e.g. "Consignments.Read" */
  permission: string;
  /** Human-readable section name shown in the denied message */
  sectionName?: string;
  children: ReactNode;
}

const ACTION_COLORS: Record<PermissionAction, string> = {
  Read: "blue",
  Create: "green",
  Update: "orange",
  Delete: "red"
};

export const PermissionGate = ({ permission, sectionName, children }: PermissionGateProps) => {
  const user = useAuthStore((x) => x.user);
  const navigate = useNavigate();

  const userPermissions = user?.permissions ?? [];
  const userRoles = user?.roles ?? [];

  if (hasPermission(userPermissions, userRoles, permission)) {
    return <>{children}</>;
  }

  const [module, action] = permission.split(".");
  const summary = getPermissionSummary(userPermissions, userRoles);
  const displayName = sectionName ?? module ?? permission;

  return (
    <Card className="panel-card">
      <div
        style={{
          maxWidth: 680,
          margin: "32px auto",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          textAlign: "center"
        }}
      >
        {/* Icon */}
        <div
          style={{
            width: 72,
            height: 72,
            borderRadius: "50%",
            background: "rgba(255,77,79,0.1)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            marginBottom: 20
          }}
        >
          <LockOutlined style={{ fontSize: 32, color: "#ff4d4f" }} />
        </div>

        {/* Heading */}
        <Title level={3} style={{ margin: 0 }}>Access Restricted</Title>
        <Text type="secondary" style={{ fontSize: 15, marginTop: 8, display: "block" }}>
          You don't have permission to access <strong>{displayName}</strong> in this organisation.
        </Text>

        {/* Required permission box */}
        <div
          style={{
            marginTop: 24,
            padding: "14px 24px",
            borderRadius: 8,
            border: "1px solid var(--error, #ff4d4f)",
            background: "rgba(255,77,79,0.04)",
            width: "100%",
            textAlign: "left"
          }}
        >
          <Text style={{ fontSize: 12, textTransform: "uppercase", letterSpacing: 1, fontWeight: 700, color: "#ff4d4f" }}>
            Required Permission
          </Text>
          <div style={{ marginTop: 8, display: "flex", alignItems: "center", gap: 8 }}>
            <Tag color="red" style={{ fontSize: 14, padding: "2px 10px" }}>
              {module} › {action}
            </Tag>
            {action && ACTION_DESCRIPTIONS[action as PermissionAction] && (
              <Text type="secondary" style={{ fontSize: 13 }}>
                — {ACTION_DESCRIPTIONS[action as PermissionAction]}
              </Text>
            )}
          </div>
        </div>

        <Divider style={{ margin: "24px 0 20px" }} />

        {/* Current access summary */}
        <div style={{ width: "100%", textAlign: "left" }}>
          <Text style={{ fontSize: 12, textTransform: "uppercase", letterSpacing: 1, fontWeight: 700, color: "#888" }}>
            Your Current Access
          </Text>
          <div style={{ marginTop: 12, display: "flex", flexDirection: "column", gap: 8 }}>
            {PERMISSION_MODULES.map((mod) => {
              const granted = summary[mod];
              return (
                <div
                  key={mod}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    padding: "6px 12px",
                    borderRadius: 6,
                    background: granted.length > 0 ? "rgba(82,196,26,0.06)" : "rgba(0,0,0,0.03)",
                    border: `1px solid ${granted.length > 0 ? "rgba(82,196,26,0.2)" : "var(--app-bg, #f0f0f0)"}`
                  }}
                >
                  <Text strong style={{ fontSize: 13, minWidth: 110 }}>{mod}</Text>
                  {granted.length > 0 ? (
                    <Space size={4} wrap>
                      {granted.map((a) => (
                        <Tooltip placement="bottom" key={a} title={ACTION_DESCRIPTIONS[a]}>
                          <Tag color={ACTION_COLORS[a]} style={{ fontSize: 11, margin: 0 }}>{a}</Tag>
                        </Tooltip>
                      ))}
                    </Space>
                  ) : (
                    <Tag color="default" style={{ fontSize: 11, margin: 0 }}>No access</Tag>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        <Divider style={{ margin: "24px 0 20px" }} />

        {/* Call to action */}
        <div
          style={{
            padding: "14px 20px",
            borderRadius: 8,
            background: "rgba(24,144,255,0.05)",
            border: "1px solid rgba(24,144,255,0.2)",
            width: "100%",
            textAlign: "left"
          }}
        >
          <Space align="start" size={10}>
            <MailOutlined style={{ color: "#1890ff", fontSize: 16, marginTop: 2 }} />
            <div>
              <Text strong style={{ fontSize: 13 }}>How to get access</Text>
              <Paragraph style={{ margin: "4px 0 0", fontSize: 13, color: "#555" }}>
                Contact your system administrator and ask them to grant you the{" "}
                <Text code style={{ fontSize: 12 }}>{permission}</Text>{" "}
                permission via User Management → Permissions.
              </Paragraph>
            </div>
          </Space>
        </div>

        {/* Navigation buttons */}
        <Space style={{ marginTop: 24 }}>
          <Button type="primary" onClick={() => navigate("/")}>
            Go to Dashboard
          </Button>
          <Button onClick={() => navigate(-1)}>
            Go Back
          </Button>
        </Space>
      </div>
    </Card>
  );
};
